import { pull } from 'lodash';
import { DossierId } from '@/dossier/models/AbstractDossier';
import User from '@/shared/models/User';
import { isPresent, Nullish } from '@/shared/utils/typescript';
import InvoicingPeriod from '../constants/InvoicingPeriod';
import TaskCategory, { TASK_TYPES_BY_CATEGORY } from '../constants/TaskCategory';
import TaskStatus from '../constants/TaskStatus';
import AssignedDossierTask from './AssignedDossierTask';
import CleaningTask from './CleaningTask';
import Counters from './Counters';
import DossierStack, { DossierStackId } from './DossierStack';
import DossierStackItem from './DossierStackItem';
import InvoicingSummary from './InvoicingSummary';
import InvoicingTask from './InvoicingTask';
import Memo from './Memo';
import PlanningTask from './PlanningTask';
import Presence from './Presence';
import Task, { TaskId } from './Task';
import TaskContainer from './TaskContainer';
import TaskContext from './TaskContext';
import TaskFilters from './TaskFilters';

export default class TaskOverviewContext extends TaskContext {
  planningTasks: Array<PlanningTask> = [];
  counters = Counters.EMPTY;
  presences: Array<Presence> = [];
  invoicingSummary: InvoicingSummary | null = null;
  invoicingPeriod: InvoicingPeriod | null = null;
  category: TaskCategory | null = null;
  assignedDossierTasks: Array<AssignedDossierTask> = [];
  cleaningTasks: Array<CleaningTask> = [];
  stacks: Array<DossierStack> = [];
  currentStack: DossierStack | null = null;
  currentStackItems: Array<DossierStackItem> = [];

  constructor(
    readonly filters: TaskFilters,
  ) {
    super();
  }

  getTasksByCategory(category: Nullish<TaskCategory>): Array<Task> {
    if (!category) return this.tasks;
    return this.tasks.filter(task => TASK_TYPES_BY_CATEGORY[category].includes(task.type));
  }

  getCurrentPresence(): Presence | undefined {
    const outletIds = this.filters.common.activeOutletIds;
    if (outletIds.length === 1) {
      return this.presences.find(p => p.outletId === outletIds[0]);
    }
  }

  isNotPresentAnywhere(): boolean {
    return this.presences.every(p => !p.isPresent);
  }

  getCurrentInvoicingTasks(): Array<InvoicingTask> {
    return this.invoicingSummary?.tasks.filter(t => t.matchesPeriod(this.invoicingPeriod) && t.matchesTags(this.filters.common.activeDossierTagIds)) ?? [];
  }

  setTaskContainer(container: TaskContainer): void {
    this.tasks = container.tasks;
    this.presences = container.presences;
    this.counters = container.counters;
    this.planningTasks = container.planningTasks;
    this.stacks = container.stacks;
  }

  setAssignedDossierTasks(tasks: Array<AssignedDossierTask>): void {
    this.assignedDossierTasks = tasks;
    this.counters.vehiclesExpected = tasks.filter(d => d.isWaitingForCheckIn).length;
    this.counters.assignedDossiers = tasks.length - this.counters.vehiclesExpected;
  }

  resolveTask(taskId: TaskId): void {
    if (this.filters.status !== TaskStatus.RESOLVED) {
      this.removeTask(taskId);
    } else {
      super.resolveTask(taskId);
    }
  }

  resolveMemo(memo: Memo, user: User): void {
    super.resolveMemo(memo, user);
    this.decrementMemoCounter();
  }

  removeMemo(memo: Memo): void {
    super.removeMemo(memo);
    this.decrementMemoCounter();
  }

  decrementMemoCounter(): void {
    if (this.counters.memos > 0) --this.counters.memos;
  }

  removeInvoicingTask(dossierId: DossierId): void {
    const tasks = this.invoicingSummary?.tasks ?? [];
    const task = tasks.find(i => i.dossier?.id === dossierId);

    if (task) {
      this.invoicingSummary?.subtotals.addAmount(-task.amount, task.period);
      pull(tasks, task);
    }
  }

  updateStackSize(stackId: Nullish<DossierStackId>, size: number | null): void {
    const stack = isPresent(stackId) && this.stacks.find(s => s.id === stackId);
    if (stack) stack.size = size;
  }
}